import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SettingsService, ProjectSettings } from '../services/settings.service';
import { ToastService } from '../services/toast.service';
import { I18nService } from '../services/i18n.service';

@Component({
  selector: 'app-project-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-100 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-blue-600"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
            {{ i18n.t('settings.title') }}
          </h2>
          <button (click)="onClose.emit()" class="btn-modal-close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" x2="6" y1="6" y2="18"/><line x1="6" x2="18" y1="6" y2="18"/></svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 overflow-y-auto space-y-8 flex-1">
          <!-- Assets Section -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Logo Upload -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-700">{{ i18n.t('settings.logo') }}</label>
              <div class="relative group h-32">
                <div class="w-full h-full bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center overflow-hidden transition-all group-hover:border-slate-400 group-hover:bg-slate-50">
                  @if (tempSettings.logoUrl) {
                  <img [src]="tempSettings.logoUrl" class="h-full w-full object-contain p-4" alt="Project Logo">
                  <div class="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button (click)="$event.stopPropagation(); removeAsset('logo')" class="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-lg" title="Rimuovi">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    </button>
                  </div>
                  } @else {
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 text-gray-400 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                  <span class="text-xs text-gray-500">{{ i18n.t('settings.upload') }}</span>
                  }
                  <input type="file" (change)="uploadAsset($event, 'logo')" class="absolute inset-0 opacity-0 cursor-pointer z-0">
                </div>
              </div>
            </div>

            <!-- Background Upload -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-gray-700">{{ i18n.t('settings.background') }}</label>
              <div class="relative group h-32">
                <div class="w-full h-full bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center overflow-hidden transition-all group-hover:border-slate-400 group-hover:bg-slate-50">
                  @if (tempSettings.backgroundUrl) {
                  <img [src]="tempSettings.backgroundUrl" class="h-full w-full object-cover" alt="Background">
                  <div class="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                     <button (click)="$event.stopPropagation(); removeAsset('background')" class="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-lg" title="Rimuovi">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    </button>
                  </div>
                  } @else {
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 text-gray-400 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12A9 9 0 1 0 3 12a9 9 0 0 0 18 0Z"/><path d="M12 9v6"/><path d="M9 12h6"/></svg>
                  <span class="text-xs text-gray-500">{{ i18n.t('settings.upload') }}</span>
                  }
                  <input type="file" (change)="uploadAsset($event, 'background')" class="absolute inset-0 opacity-0 cursor-pointer z-0">
                </div>
              </div>
            </div>
          </div>

          <!-- Palette Section -->
          <div class="space-y-4">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wider">{{ i18n.t('settings.palette') }}</h3>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <!-- Primary -->
              <div class="space-y-2">
                <label class="block text-xs text-gray-500 font-medium">{{ i18n.t('settings.primary') }}</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="tempSettings.primaryColor" (change)="onPreview()" class="w-10 h-10 rounded-lg overflow-hidden border-0 p-0 cursor-pointer">
                  <input type="text" [(ngModel)]="tempSettings.primaryColor" (change)="onPreview()" class="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                </div>
              </div>
              <!-- Secondary -->
              <div class="space-y-2">
                <label class="block text-xs text-gray-500 font-medium">{{ i18n.t('settings.secondary') }}</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="tempSettings.secondaryColor" (change)="onPreview()" class="w-10 h-10 rounded-lg overflow-hidden border-0 p-0 cursor-pointer">
                  <input type="text" [(ngModel)]="tempSettings.secondaryColor" (change)="onPreview()" class="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                </div>
              </div>
              <!-- Accent -->
              <div class="space-y-2">
                <label class="block text-xs text-gray-500 font-medium">{{ i18n.t('settings.accent') }}</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="tempSettings.accentColor" (change)="onPreview()" class="w-10 h-10 rounded-lg overflow-hidden border-0 p-0 cursor-pointer">
                  <input type="text" [(ngModel)]="tempSettings.accentColor" (change)="onPreview()" class="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                </div>
              </div>
            </div>
            <!-- Labels Section -->
            <div class="pt-4 border-t border-gray-100 space-y-4">
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wider">{{ i18n.t('settings.labels') }}</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <!-- Staff Label -->
                <div class="space-y-2">
                  <label class="block text-xs text-gray-500 font-medium">{{ i18n.t('settings.staffLabel') }}</label>
                  <input type="text" [(ngModel)]="tempSettings.staffLabel" class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                </div>
                <!-- Customer Label -->
                <div class="space-y-2">
                  <label class="block text-xs text-gray-500 font-medium">{{ i18n.t('settings.customerLabel') }}</label>
                  <input type="text" [(ngModel)]="tempSettings.customerLabel" class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button 
            (click)="onCancel()"
            class="btn-system-secondary"
          >
            {{ i18n.t('settings.cancel') }}
          </button>
          <button 
            (click)="onSave()"
            [disabled]="saving"
            class="btn-system-primary"
          >
            {{ saving ? i18n.t('settings.saving') : i18n.t('settings.save') }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    input[type="color"]::-webkit-color-swatch-wrapper { padding: 0; }
    input[type="color"]::-webkit-color-swatch { border: none; }
  `]
})
export class ProjectSettingsComponent implements OnInit {
  @Output() onClose = new EventEmitter<void>();

  tempSettings!: ProjectSettings;
  saving = false;

  constructor(
    private settingsService: SettingsService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    public i18n: I18nService
  ) { }

  ngOnInit() {
    // Clone settings for editing
    this.settingsService.settings$.subscribe(s => {
      this.tempSettings = { ...s };
    });
  }

  onPreview() {
    this.settingsService.previewSettings(this.tempSettings);
  }

  onCancel() {
    this.settingsService.cancelChanges();
    this.onClose.emit();
  }

  async onSave() {
    this.saving = true;
    this.settingsService.updateSettings(this.tempSettings).subscribe({
      next: () => {
        this.toastService.success(this.i18n.t('toast.settingsSaved'));
        this.saving = false;
        this.onClose.emit();
      },
      error: () => {
        this.toastService.error(this.i18n.t('toast.settingsError'));
        this.saving = false;
      }
    });
  }

  removeAsset(type: 'logo' | 'background') {
    const oldUrl = type === 'logo' ? this.tempSettings.logoUrl : this.tempSettings.backgroundUrl;
    if (oldUrl) {
      this.settingsService.deleteAsset(oldUrl).subscribe();
    }

    if (type === 'logo') {
      this.tempSettings.logoUrl = null;
    } else {
      this.tempSettings.backgroundUrl = null;
    }
    this.onPreview();
    this.cdr.detectChanges();
  }

  uploadAsset(event: any, type: 'logo' | 'background') {
    const file = event.target.files[0];
    if (file) {
      // Delete old one if exists before uploading new
      const oldUrl = type === 'logo' ? this.tempSettings.logoUrl : this.tempSettings.backgroundUrl;

      this.settingsService.uploadAsset(file).subscribe({
        next: (res) => {
          if (res.data && res.data.length > 0) {
            // After success upload, delete old if it was there
            if (oldUrl) {
              this.settingsService.deleteAsset(oldUrl).subscribe();
            }

            if (type === 'logo') {
              this.tempSettings.logoUrl = res.data[0];
            } else {
              this.tempSettings.backgroundUrl = res.data[0];
            }
            this.onPreview();
            this.cdr.detectChanges();
            this.toastService.success('1 ' + this.i18n.t('toast.assetUploaded'));
          }
        },
        error: () => {
          this.toastService.error(this.i18n.t('toast.assetError'));
        }
      });
    }
  }
}
